import { useCallback, useMemo, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import { MinusIcon } from '@heroicons/react/24/outline';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { DeleteModal } from 'src/common/DeleteModal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import { useBulkUnassignTrips } from 'src/features/dispatch/modals/useBulkUnassignTrips';

export function UnassignAllTripsMenuItem() {
  const dispatch = useAppDispatch();

  const dispatchResponse = useAppSelector(
    (state) => state.dispatcher.dispatchResponse
  );
  const assignmentIds = useMemo(
    () =>
      dispatchResponse?.assignments.map((assignment) => assignment.id) ?? [],
    [dispatchResponse]
  );
  const assignedTrips = useMemo(
    () => dispatchResponse?.assigned_trips.map((trip) => trip.id) ?? [],
    [dispatchResponse]
  );

  const { handleBulkUnassignTrips } = useBulkUnassignTrips();
  const [showModal, setShowModal] = useState(false);

  const unAssignAllTrips = useCallback(async () => {
    setShowModal(false);
    dispatch(
      show({
        title: 'Unassigning all trips',
        description: 'This may take a few minutes to complete',
        type: 'info',
      })
    );

    const result = await handleBulkUnassignTrips(assignmentIds);

    if (result.failedCount > 0) {
      dispatch(
        show({
          title:
            'Error unassigning all trips, some trips may not have been unassigned',
          description: `Failed to unassign trips from the ${result.failedCount} assignments`,
          type: 'error',
        })
      );
    } else {
      dispatch(
        show({
          title: 'All trips unassigned',
          description: 'All trips have been unassigned',
          type: 'success',
        })
      );
    }
  }, [assignmentIds, dispatch, handleBulkUnassignTrips, setShowModal]);

  return (
    <div
      className="flex w-full items-center"
      data-testid="unassign-all-trips-menu-item"
    >
      <SimpleTooltip
        trigger={
          <div className="flex">
            <Button
              dataTestId="dispatch-create-route-templates"
              aria-label="Unassign all trips"
              onClick={() => {
                setShowModal(true);
              }}
              className="flex flex-row gap-2 !p-0 py-2 !text-xs"
              variant="ghost"
              disabled={!assignedTrips.length || !assignmentIds.length}
            >
              <MinusIcon className="h-4 w-4" />
              Unassign all trips
            </Button>
          </div>
        }
        content={
          !assignedTrips.length || !assignmentIds.length
            ? 'No assigned trips'
            : null
        }
      />
      <DeleteModal
        open={showModal}
        setOpen={setShowModal}
        onCancel={() => setShowModal(false)}
        onConfirm={unAssignAllTrips}
        title="Unassign all trips"
        description="Are you sure you want to unassign all trips? This action cannot be undone."
        confirmText="Unassign"
      />
    </div>
  );
}
